import { Box, Card, CardProps, styled, Theme, useTheme } from "@mui/material";

import BybAnimatedBorder from "@Components/common/byb-ds/byb-animated-border";

interface IBybVrCard extends CardProps {
  children: React.ReactNode;
  highlight?: boolean;
  highlightColor?: string;
  onClick?: () => void;
}

const wrapperStyles = (theme: Theme) => ({
  borderRadius: theme.spacing(4),
  height: "100%",
  width: "100%",
});

const hoverStyles = () => ({
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-2px)",
  },
});

const getCardCornerShape = (theme: Theme) => {
  const rootOverride = theme.components?.MuiCard?.styleOverrides?.root;
  const rootStyles = typeof rootOverride === "function" ? rootOverride({ theme, ownerState: {} } as never) : rootOverride;

  if (!rootStyles || typeof rootStyles !== "object" || Array.isArray(rootStyles)) {
    return undefined;
  }

  const cornerShape = "cornerShape" in rootStyles ? rootStyles.cornerShape : undefined;

  return typeof cornerShape === "string" ? cornerShape : undefined;
};

const StyledBybVrCard = styled(Card)<IBybVrCard>(({ theme, onClick }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  padding: theme.spacing(3.5),
  ...(onClick && {
    backgroundColor: `${theme.palette.dsSurface.v100}CC`,
    borderColor: `${theme.palette.dsSurface.v400}CC`,
    cursor: "pointer",
    boxShadow: `0 8px 16px ${theme.palette.dsSurface.v500}40`,
  }),
}));

const BybVrCard = ({ children, highlight, highlightColor, onClick, ...props }: IBybVrCard) => {
  const theme = useTheme();
  const card = (
    <StyledBybVrCard onClick={onClick} {...props}>
      {children}
    </StyledBybVrCard>
  );

  if (!highlight) {
    return <Box sx={{ ...wrapperStyles(theme), ...(onClick && hoverStyles()) }}>{card}</Box>;
  }

  return (
    <BybAnimatedBorder
      borderRadius={theme.spacing(4)}
      color={highlightColor}
      sx={{
        ...wrapperStyles(theme),
        ...(onClick && hoverStyles()),
        cornerShape: getCardCornerShape(theme),
      }}
    >
      {card}
    </BybAnimatedBorder>
  );
};

export default BybVrCard;
